import { ParticipantsService } from "@/common/api.service";
import {
  DESTROY_PARTICIPANT,
  DESTROY_ALL_PARTICIPANTS,
  IGNORE_ALL_PARTICIPANTS,
  FETCH_INVALID_PARTICIPANTS,
  CALCULATE_PARTICIPANTS,
  SAVE_PARTICIPANTS,
  UPDATE_PARTICIPANT,
  FETCH_ANNUAL_COST_ESTIMATION,
  FETCH_COMPENSATION_SEGMENTS,
  GO_NEXT_STEP,
  UPLOAD_FIXED_PARTICIPANTS,
  DOWNLOAD_INVALID_PARTICIPANTS
} from "./actions.type";
import {
  VALIDATE_PARTICIPANTS_END,
  VALIDATE_PARTICIPANTS_START,
  VALIDATE_PARTICIPANTS_FAILURE,
  DELETE_PARTICIPANT,
  DELETE_ALL_PARTICIPANT,
  RESET_PARTICIPANTS,
  SET_ANNUAL_COST_ESTIMATION,
  SET_COMPENSATION_SEGMENTS,
  SET_INVALID_FORM_STATE,
  GO_TO_COMPENSATION_SEGMENTS,
  HIDE_FORM_ERRORS,
  DELETE_PARTICIPANT_FIELD,
  SET_PARTICIPANTS_ERRORS_INFO,
  FLASH_MESSAGE_SET_WARNING_MESSAGE,
  FLASH_MESSAGE_SET_ERROR_MESSAGE,
  START_LOADING,
  END_LOADING
} from "./mutations.type";
import router from "@/router";

const state = {
  isLoading: false,
  participantFile: null,
  structureErrors: null,
  maxDeferralRate: null,
  errorsInfo: {},
  activeErrors: [],
  terminatedErrors: [],
  terminatedBeforeCurrentYearErrors: [],
  activeDeferralRate: [],
  terminatedDeferralRate: [],
  annualCostEstimation: {},
  compensationSegments: []
};

function parseErrors(errors) {
  return errors.map(ob => JSON.parse(ob)).sort((a, b) => a.id - b.id);
}

const getters = {
  isParticipantsLoading(state) {
    return state.isLoading;
  },
  structureErrors(state) {
    return state.structureErrors;
  },
  isDataValid(state) {
    return (
      (
        state.activeErrors +
        state.terminatedErrors +
        state.terminatedBeforeCurrentYearErrors +
        state.activeDeferralRate +
        state.terminatedDeferralRate
      ).length <= 0
    );
  },
  activeErrors(state) {
    return state.activeErrors;
  },
  activeDeferralRate(state) {
    return state.activeDeferralRate;
  },
  terminatedDeferralRate(state) {
    return state.terminatedDeferralRate;
  },
  terminatedErrors(state) {
    return state.terminatedErrors;
  },
  terminatedBeforeCurrentYearErrors(state) {
    return state.terminatedBeforeCurrentYearErrors;
  },
  maxDeferralRate(state) {
    return state.maxDeferralRate;
  },
  errorsInfo(state) {
    return state.errorsInfo;
  },
  participantFile(state) {
    return state.participantFile;
  },
  annualCostEstimation(state) {
    return state.annualCostEstimation;
  },
  compensationSegments(state) {
    return state.compensationSegments;
  }
};

const actions = {
  async [FETCH_INVALID_PARTICIPANTS](context, reportId) {
    return ParticipantsService.invalidParticipants(reportId)
      .then(({ data }) => {
        if (data.participant_file_name) {
          context.commit(SET_INVALID_FORM_STATE, { isInvalid: false });
          context.state.participantFile = { name: data.participant_file_name };
        }
        if (data.errors) {
          context.commit(VALIDATE_PARTICIPANTS_FAILURE, data.errors);
          context.commit(SET_INVALID_FORM_STATE, { isInvalid: true });
        }
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [SAVE_PARTICIPANTS](context, { reportId, file, includeTerminated }) {
    context.commit(VALIDATE_PARTICIPANTS_START, file);
    return ParticipantsService.save(reportId, file, includeTerminated)
      .then(({ data }) => {
        context.commit(FLASH_MESSAGE_SET_WARNING_MESSAGE, data.warning_message);
        context.commit(VALIDATE_PARTICIPANTS_END);
        context.commit(HIDE_FORM_ERRORS);
        context.dispatch(GO_NEXT_STEP);
        context.commit(GO_TO_COMPENSATION_SEGMENTS, reportId);
      })
      .catch(({ response }) => {
        context.commit(VALIDATE_PARTICIPANTS_FAILURE, response.data.errors);
        context.commit(FLASH_MESSAGE_SET_ERROR_MESSAGE, response.data.errors);
        context.commit(SET_INVALID_FORM_STATE, { isInvalid: true });
      });
  },
  async [DOWNLOAD_INVALID_PARTICIPANTS](context, reportId) {
    ParticipantsService.downloadErrors(reportId);
  },
  async [UPLOAD_FIXED_PARTICIPANTS](context, { reportId, file }) {
    context.commit(VALIDATE_PARTICIPANTS_START, file);
    return ParticipantsService.uploadFixed(reportId, file)
      .then(() => {
        context.commit(VALIDATE_PARTICIPANTS_END);
        context.commit(HIDE_FORM_ERRORS);
        context.dispatch(GO_NEXT_STEP);
        context.commit(GO_TO_COMPENSATION_SEGMENTS, reportId);
      })
      .catch(({ response }) => {
        context.commit(VALIDATE_PARTICIPANTS_FAILURE, response.data.errors);
        context.commit(SET_INVALID_FORM_STATE, { isInvalid: true });
      });
  },
  async [DESTROY_PARTICIPANT](context, { reportId, id }) {
    return ParticipantsService.destroy(reportId, id)
      .then(({ data }) => {
        context.commit(DELETE_PARTICIPANT, id);
        context.commit(SET_PARTICIPANTS_ERRORS_INFO, data.errors_info);
        if (context.getters.isDataValid)
          context.dispatch(CALCULATE_PARTICIPANTS, reportId);
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [UPDATE_PARTICIPANT](context, { reportId, params }) {
    return ParticipantsService.update(reportId, params.id, params)
      .then(({ data }) => {
        if (data.valid) {
          context.commit(SET_PARTICIPANTS_ERRORS_INFO, data.errors_info);
          context.commit(DELETE_PARTICIPANT, params.id);
        } else
          context.commit(DELETE_PARTICIPANT_FIELD, data.participant_errors);
        if (context.getters.isDataValid)
          context.dispatch(CALCULATE_PARTICIPANTS, reportId);
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [DESTROY_ALL_PARTICIPANTS](context, { reportId, errorType }) {
    context.commit(DELETE_ALL_PARTICIPANT, errorType);
    return ParticipantsService.destroy_all(reportId, errorType)
      .then(({ data }) => {
        context.commit(SET_PARTICIPANTS_ERRORS_INFO, data.errors_info);
        if (context.getters.isDataValid)
          context.dispatch(CALCULATE_PARTICIPANTS, reportId);
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [IGNORE_ALL_PARTICIPANTS](context, { reportId, errorType }) {
    context.commit(START_LOADING);
    return ParticipantsService.ignore_all(reportId, errorType)
      .then(() => {
        context.commit(END_LOADING);
        context.commit(HIDE_FORM_ERRORS);
        context.dispatch(GO_NEXT_STEP);
        context.commit(GO_TO_COMPENSATION_SEGMENTS, reportId);
      })
      .catch(({ response }) => {
        context.commit(VALIDATE_PARTICIPANTS_FAILURE, response.data.errors);
        context.commit(SET_INVALID_FORM_STATE, { isInvalid: true });
      });
  },
  async [CALCULATE_PARTICIPANTS](context, reportId) {
    context.commit(VALIDATE_PARTICIPANTS_START, context.state.participantFile);
    return ParticipantsService.calculate(reportId)
      .then(({ data }) => {
        context.commit(VALIDATE_PARTICIPANTS_END);
        context.commit(HIDE_FORM_ERRORS);
        context.dispatch(GO_NEXT_STEP);
        context.commit(GO_TO_COMPENSATION_SEGMENTS, reportId);
        context.commit(FLASH_MESSAGE_SET_WARNING_MESSAGE, data.warning_message);
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [FETCH_COMPENSATION_SEGMENTS](context, reportId) {
    return ParticipantsService.compensationSegments(reportId)
      .then(({ data }) => {
        context.commit(SET_COMPENSATION_SEGMENTS, data);
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  async [FETCH_ANNUAL_COST_ESTIMATION](context, reportId) {
    return ParticipantsService.getAnnualCostEstimation(reportId)
      .then(({ data }) => {
        context.commit(SET_ANNUAL_COST_ESTIMATION, data);
      })
      .catch(error => {
        throw new Error(error);
      });
  }
};

const mutations = {
  [START_LOADING](state) {
    state.isLoading = true;
  },
  [END_LOADING](state) {
    state.isLoading = false;
  },
  [VALIDATE_PARTICIPANTS_START](state, file) {
    state.isLoading = true;
    state.structureErrors = null;
    state.errorsInfo = {};
    state.maxDeferralRate = null;
    state.activeErrors = [];
    state.terminatedErrors = [];
    state.terminatedBeforeCurrentYearErrors = [];
    state.activeDeferralRate = [];
    state.terminatedDeferralRate = [];
    state.participantFile = file;
  },
  [VALIDATE_PARTICIPANTS_END](state) {
    state.isLoading = false;
  },
  [VALIDATE_PARTICIPANTS_FAILURE](state, errors) {
    if (typeof errors === "object") {
      if (errors.structure_errors) {
        state.structureErrors = errors.structure_errors;
      } else {
        state.errorsInfo = errors.info;
        state.maxDeferralRate = errors.max_deferral_rate;
        state.activeErrors = parseErrors(errors.active);
        state.terminatedErrors = parseErrors(errors.terminated);
        state.terminatedBeforeCurrentYearErrors = parseErrors(
          errors.terminated_before_current_year
        );
        state.activeDeferralRate = parseErrors(errors.active_deferral_rate);
        state.terminatedDeferralRate = parseErrors(
          errors.terminated_deferral_rate
        );
      }
    } else {
      state.participantFile = null;
    }
    state.isLoading = false;
  },
  [DELETE_PARTICIPANT](state, id) {
    state.activeErrors = state.activeErrors.filter(error => error.id !== id);
    state.terminatedErrors = state.terminatedErrors.filter(
      error => error.id !== id
    );
    state.terminatedBeforeCurrentYearErrors = state.terminatedBeforeCurrentYearErrors.filter(
      error => error.id !== id
    );
    state.activeDeferralRate = state.activeDeferralRate.filter(
      error => error.id !== id
    );
    state.terminatedDeferralRate = state.terminatedDeferralRate.filter(
      error => error.id !== id
    );
  },
  [DELETE_PARTICIPANT_FIELD](state, participantErrors) {
    let participantIndex;

    participantIndex = state.activeErrors.findIndex(
      er => er.id === participantErrors.id
    );
    if (participantIndex >= 0) {
      Object.assign(state.activeErrors[participantIndex], participantErrors);
      return;
    }

    participantIndex = state.terminatedErrors.findIndex(
      er => er.id === participantErrors.id
    );
    if (participantIndex >= 0) {
      Object.assign(
        state.terminatedErrors[participantIndex],
        participantErrors
      );
      return;
    }

    participantIndex = state.terminatedBeforeCurrentYearErrors.findIndex(
      er => er.id === participantErrors.id
    );
    if (participantIndex >= 0) {
      Object.assign(
        state.terminatedBeforeCurrentYearErrors[participantIndex],
        participantErrors
      );
      return;
    }
  },
  [DELETE_ALL_PARTICIPANT](state, errorType) {
    switch (errorType) {
      case "ap_invalid":
        state.activeErrors = [];
        break;
      case "tp_invalid":
        state.terminatedErrors = [];
        break;
      case "tp_before_current_year_invalid":
        state.terminatedBeforeCurrentYearErrors = [];
        break;
      case "ap_deferral_rate":
        state.activeDeferralRate = [];
        break;
      case "tp_deferral_rate":
        state.terminatedDeferralRate = [];
        break;
    }
  },
  [GO_TO_COMPENSATION_SEGMENTS](state, reportId) {
    router.push({
      name: "report-participants",
      params: {
        id: reportId,
        step: "basic-data"
      }
    });
  },
  [RESET_PARTICIPANTS](state) {
    state.participantFile = null;
    state.structureErrors = null;
    state.errorsInfo = {};
    state.maxDeferralRate = null;
    state.activeErrors = [];
    state.terminatedErrors = [];
    state.terminatedBeforeCurrentYearErrors = [];
    state.activeDeferralRate = [];
    state.terminatedDeferralRate = [];
  },
  [SET_ANNUAL_COST_ESTIMATION](state, annualCost) {
    state.annualCostEstimation = annualCost;
  },
  [SET_COMPENSATION_SEGMENTS](state, segments) {
    state.compensationSegments = segments;
  },
  [SET_PARTICIPANTS_ERRORS_INFO](state, errorsInfo) {
    state.errorsInfo = errorsInfo;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
